import {
    AlarmSubtract, Asset, AssetConfirm, CalendarHeatMap, CloudDataOps, EarthEuropeAfrica,
    Event, EventSchedule, GasStation, Hourglass, Meter, Notification, Snooze, Sustainability,
    Timer, UserIdentification, VehicleInsights, Warning, Activity
} from '@carbon/icons-react';

// APP CONSTATNS
const app = {
    DEBUG: false,
    MOCK: false,
    APP_NAME: process.env.REACT_APP_APP_NAME,
    API_URL: process.env.REACT_APP_API_URL,
    OSM_URL: process.env.REACT_APP_OSM_URL,
    STREAMING_SAVER_MITM: process.env.REACT_APP_STREAMING_MITM ?? "https://stag.app.fleetclear.com/streamsaver/mitm.html",
    AUTH_KEY: 'iss-key',
    API_LOGIN_KEY: 'iss-api-key',
    SHARD_KEY: 'shard-id',
    LOCALSTORAGE_EXPIRES_KEY: 'expire-auth',
    GOOGLE_MAP_API_KEY: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    GOOGLE_MAP_ID: process.env.REACT_APP_GOOGLE_MAP_ID,
    TOMTOM_API_KEY: process.env.REACT_APP_TOMTOM_API_KEY,
    GOOGLE_PATH_BLUE: '#1a73e8',
    DEFAULT_MAP_CENTER: { // Center of UK
        lat: 55.0022,
        lng: -2.5420,
    },
    DEFAULT_MAP_ZOOM: 6,
    DEFAULT_MAP_ZOOM_MAP_LIBRE: 5,
    DEFAULT_MAP_CENTER_MAP_LIBRE: { // Center of UK
        lat: 54.5,
        lng: -3.0420,
    },
    LOCALSTORAGE_EXPIRES: '30', // minutes
    MAP_SLIDER_INTERVAL: parseInt(process.env.REACT_APP_MAP_SLIDER_INTERVAL, 10),
    MAP_LIVE_ASSET_DELAY: parseInt(process.env.REACT_APP_MAP_LIVE_ASSET_DELAY, 10),
    GEOFENCE_REPORT_FEATURE: 'geofence-report',
    SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN,

    /* DISTANCE */
    KM_TO_MILES_FACTOR: 1.609344,

    /* TIME AND DATE */
    SECONDS_IN_A_DAY: 86400,
    ZERO_INDEXED_SECONDS_IN_A_DAY: 86399,
    DB_DATETIME_FORMAT: 'YYYY-MM-DD HH:mm:ss',
    DB_DATE_FORMAT: 'YYYY-MM-DD',
    DB_TIME_FORMAT: 'HH:mm:ss',
    TIME_FORMAT_HOURS_MINS: 'HH:mm',
    USER_DATETIME_FORMAT: 'DD/MM/YYYY h:mm:ss A',

    /* SIZING */
    PADDING_1: 8, // matches ml-1, mr-1, mt-1, mb-1
    PADDING_2: 16, // matches ml-2, mr-2, mt-2, mb-2
    PADDING_3: 24, // matches ml-3, mr-3, mt-3, mb-3
    PADDING_4: 32, // matches ml-4, mr-4, mt-4, mb-4

    FUEL_TYPES: [
        'Diesel',
        'Petrol',
        'Gas',
        'Electricity',
        'Hydrogen',
        'Hybrid',
    ],

    /* REGEX */
    REGEX_VALID_EMAIL: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    REGEX_VALID_UK_POSTCODE: /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/,
    REGEX_VALID_NUMBER: /^[0-9]*$/,
    REGEX_VALID_HOURS_MINS: /^(0\d|1\d|2[0-3]):[0-5]\d$/,

    TIMELINE_DATE_FORMAT: 'hh:mm:ss a',

    REPORT_TYPES: [
        {
            id: 1,
            link: 'mileage-report',
            feature_id: 'mileage-report',
            title: 'Mileage Report',
            description: 'Generate a mileage report for selected assets or drivers.',
            icon: Meter,
        },
        {
            id: 2,
            link: 'fuel-report',
            feature_id: 'fuel-report',
            title: 'Fuel Report',
            description: 'Generate report for selected assets or drivers. You can select from MPG, fuel volume or fuel cost.',
            icon: GasStation,
        },
        {
            id: 3,
            link: 'driving-time-report',
            feature_id: 'driving-time-report',
            title: 'Driving Time/Vehicle Usage Time Report',
            description: 'Generate a driving time/vehicle usage time report for selected assets or drivers.',
            icon: EventSchedule,
        },
        {
            id: 4,
            link: 'idle-report',
            feature_id: 'idle-report',
            title: 'Idle Report',
            description: 'Generate an idle report for selected assets or drivers.',
            icon: Snooze,
        },
        {
            id: 5,
            link: 'pto-report',
            feature_id: 'pto-report',
            title: 'PTO Report',
            description: 'Generate a pto report for selected assets or drivers.',
            icon: Asset,
        },
        {
            id: 6,
            link: 'true-idle-report',
            feature_id: 'true-idle-report',
            title: 'True Idle Report',
            description: 'Generate a true idle report for selected assets or drivers.',
            icon: AlarmSubtract,
        },
        {
            id: 7,
            link: 'full-pto-idle-report',
            feature_id: 'full-pto-idle-report',
            title: 'PTO, idle, & True Idle Report',
            description: 'Generate a report for selected dates showing a breakdown of PTO Idle & True Idle across all records.',
            icon: AssetConfirm,
        },
        {
            id: 8,
            link: 'timesheet-report',
            feature_id: 'timesheet-report',
            title: 'Timesheet Report',
            description: 'Generate a timesheet report for an asset or driver.',
            icon: CalendarHeatMap,
        },
        {
            id: 9,
            link: 'speeding-report',
            feature_id: 'speeding-report',
            title: 'Speeding Report',
            description: 'Generate a speeding report for an asset.',
            icon: Warning,
        },
        {
            id: 10,
            link: 'carbon-report',
            feature_id: 'carbon-report',
            title: 'Carbon Report',
            description: 'Generate a carbon report for an asset.',
            icon: CloudDataOps,
        },
        {
            id: 11,
            link: 'unauthorised-journey-report',
            feature_id: 'unauthorised-journey-report',
            title: 'Unauthorised Journey Report',
            description: 'Generate an unauthorised journey report.',
            icon: UserIdentification,
        },
        {
            id: 12,
            link: 'asset-odometer-report',
            feature_id: 'asset-odometer-report',
            title: 'Asset Odometer Report',
            description: 'Generate a mileage report based upon device odometer.',
            icon: VehicleInsights,
        },
        {
            id: 13,
            link: 'utilisation-report',
            feature_id: 'utilisation-report',
            title: 'Utilisation Report',
            description: 'Generate a utilisation report for assets or drivers.',
            icon: Timer,
        },
        {
            id: 14,
            link: 'eco-score-report',
            feature_id: 'eco-score-report',
            title: 'Eco Score Report',
            description: 'Generate Eco Score report',
            icon: Sustainability,
        },
        {
            id: 15,
            link: 'event-report',
            feature_id: 'event-report',
            title: 'Event Report',
            description: 'Generate Event report',
            icon: Notification,
        },
        {
            id: 16,
            link: 'geofence-report',
            feature_id: 'geofence-report',
            title: 'Geo-fence Entry/Exit Report',
            description: 'Generate Geo-fence Entry/Exit report',
            icon: EarthEuropeAfrica,
        },
        {
            id: 17,
            link: 'outside-working-hours-report',
            feature_id: 'outside-working-hours-report',
            title: 'Inside/Outside Working Hours Report',
            description: 'Generate Inside/Outside working outside hours report',
            icon: Event,
        },
        {
            id: 18,
            link: 'not-moved-report',
            feature_id: 'not-moved-report',
            title: 'Not Moved Report',
            description: 'Generate Not Moved report',
            icon: Hourglass,
        },
        {
            id: 19,
            link: 'dvr-health-report-extended',
            feature_id: 'dvr-health-report-extended',
            title: 'DVR Health Report',
            label: '14 Day Summary',
            description: 'Generate a DVR health report for an Asset',
            icon: Activity,
            type: 'dvr-health-report',
        },
        {
            id: 20,
            link: 'dvr-health-report',
            feature_id: 'dvr-health-report',
            title: 'DVR Health Report',
            label: 'Notifications List',
            description: 'Generate a DVR health report for an Asset',
            icon: Activity,
        }
    ],
    SPEEDING_ALERT_EVENT_TYPE: 918,
    DVR_ALARM_TYPES: [
        { id: 4, name: 'GPS_ALARM_TYPE_VIDEO_LOST', label: 'Camera Signal Lost Alarm Start', severity: 'Critical' },
        { id: 54, name: 'GPS_ALARM_TYPE_END_VIDEO_LOST', label: 'Camera Signal Lost Alarm End', severity: 'Critical' },
        { id: 5, name: 'GPS_ALARM_TYPE_VIDEO_MASK', label: 'Camera Occlusion Alarm', severity: 'Warning' },
        { id: 55, name: 'GPS_ALARM_TYPE_END_VIDEO_MASK', label: 'Camera Occlusion Alarm Ends', severity: 'Warning' },
        { id: 10, name: 'GPS_ALARM_TYPE_DISK_ERROR', label: 'Hard Disk Error Alarm Start', severity: 'Critical' },
        { id: 60, name: 'GPS_ALARM_TYPE_END_DISK_ERROR', label: 'Storage Waitting Load Alarm End', severity: 'Critical' },
        { id: 18, name: 'GPS_ALARM_TYPE_GPS_SIGNAL_LOSS', label: 'GPS Signal Lost Alarm Start', severity: 'Warning' },
        { id: 68, name: 'GPS_ALARM_TYPE_END_GPS_SIGNAL_LOSS', label: 'GPS Signal Lost Alarm End', severity: 'Warning' },
        { id: 39, name: 'GPS_ALARM_TYPE_DISK1_NO_EXIST', label: 'Hard Disk 1 Does Not Exist', severity: 'Warning' },
        { id: 40, name: 'GPS_ALARM_TYPE_DISK2_NO_EXIST', label: 'Hard Disk 2 Does Not Exist', severity: 'Warning' },
        { id: 162, name: 'GPS_ALARM_TYPE_DISK_SPACE_WARNING', label: 'Hard Disk Capacity Alarm', severity: 'Critical' },
        { id: 163, name: 'GPS_ALARM_TYPE_END_DISK_SPACE_WARNING', label: 'Hard Disk Capacity Alarm Ends', severity: 'Critical' }
    ],
    PAGE_SIZE_OPTIONS: [10, 25, 50],
};


export default app;